/**
 * Narrator Voice Selector Component
 *
 * Dropdown for selecting AI voices or family Voice Vault recordings
 * as the narrator for story playback.
 */

import { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
} from '@/components/ui/select';
import { useFamilyVoices } from '@/hooks/queries';
import { Mic, Volume2 } from 'lucide-react';
import { CHARACTER_VOICES, getCharacterForVoice } from '@/services/textToSpeech';

// AI voice options mapped to characters
const AI_VOICES = CHARACTER_VOICES.map((v) => ({
  id: v.voiceId,
  name: v.character,
  description: v.description,
}));

export type NarratorVoice =
  /** One of the four Yoluno character voices. */
  | { type: 'ai'; voiceId: string }
  /**
   * A cloned loved-one voice. `voiceId` is a family_member_voices row id, which
   * the server resolves against the caller's own account before synthesising.
   */
  | { type: 'family'; voiceId: string; name: string };

interface NarratorVoiceSelectorProps {
  value: NarratorVoice;
  onChange: (voice: NarratorVoice) => void;
  disabled?: boolean;
  className?: string;
}

export function NarratorVoiceSelector({
  value,
  onChange,
  disabled = false,
  className,
}: NarratorVoiceSelectorProps) {
  const { data } = useFamilyVoices();
  const familyVoices = (data?.items || []).filter((v) => v.status === 'ready');

  const currentValue = useMemo(
    () => (value.type === 'ai' ? `ai:${value.voiceId}` : `family:${value.voiceId}`),
    [value]
  );

  const handleChange = (newValue: string) => {
    if (newValue.startsWith('ai:')) {
      onChange({ type: 'ai', voiceId: newValue.slice('ai:'.length) });
      return;
    }
    const voiceId = newValue.slice('family:'.length);
    const match = familyVoices.find((v) => v.id === voiceId);
    if (match) {
      onChange({
        type: 'family',
        voiceId,
        name: match.family_member_name || match.display_name,
      });
    }
  };

  const getDisplayName = () => {
    if (value.type === 'ai') {
      const aiVoice = AI_VOICES.find((v) => v.id === value.voiceId);
      return aiVoice?.name || getCharacterForVoice(value.voiceId) || 'Story Reader';
    }
    const match = familyVoices.find((v) => v.id === value.voiceId);
    return match?.family_member_name || match?.display_name || value.name;
  };

  return (
    <Select
      value={currentValue}
      onValueChange={handleChange}
      disabled={disabled}
    >
      <SelectTrigger className={className}>
        <div className="flex items-center gap-2">
          {value.type === 'ai' ? (
            <Volume2 className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Mic className="h-4 w-4 text-primary" />
          )}
          <SelectValue>{getDisplayName()}</SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent>
        {/* AI Voices */}
        <SelectGroup>
          <SelectLabel className="flex items-center gap-2">
            <Volume2 className="h-3 w-3" />
            Story Readers
          </SelectLabel>
          {AI_VOICES.map((voice) => (
            <SelectItem key={voice.id} value={`ai:${voice.id}`}>
              <div className="flex items-center gap-2">
                <img
                  src={`/avatars/${voice.name}.png`}
                  alt={voice.name}
                  className="h-6 w-6 rounded-full object-cover"
                />
                <div className="flex flex-col">
                  <span>{voice.name}</span>
                  <span className="text-caption text-muted-foreground">
                    {voice.description}
                  </span>
                </div>
              </div>
            </SelectItem>
          ))}
        </SelectGroup>

        {/* Cloned loved-one voices */}
        {familyVoices.length > 0 && (
          <>
            <SelectSeparator />
            <SelectGroup>
              <SelectLabel className="flex items-center gap-2">
                <Mic className="h-3 w-3" />
                Family Voices
              </SelectLabel>
              {familyVoices.map((v) => (
                <SelectItem key={v.id} value={`family:${v.id}`}>
                  <div className="flex flex-col">
                    <span>{v.family_member_name || v.display_name}</span>
                    <span className="text-caption text-muted-foreground">
                      Reads in their own voice
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          </>
        )}
      </SelectContent>
    </Select>
  );
}

